import Image from "next/image";
// A locale-less `/ej-health/…` href resolves to the DEFAULT locale, which
// every page in this vertical `notFound()`s — see `arrow-button.tsx`.
import { Link } from "@/i18n/routing";
import { Play } from "lucide-react";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import { CategoryEyebrow } from "./category-badge";
import { EJHEALTH_VIDEO_ROUTE } from "@/lib/routes";
import type { VideoCard } from "@/fixtures/ej-health/fixtures";

interface Props {
  video: VideoCard;
  /** Required — see `sidebar-thumb.tsx`. Video cards default to the video
   *  detail page because that is what every one of them opens. */
  href?: string;
}

export function VideoCard({ video, href = EJHEALTH_VIDEO_ROUTE }: Props) {
  return (
    <Link href={href} className="group hkej-tap flex flex-col gap-3">
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: 16 / 9, backgroundColor: EJHEALTH_COLORS.surfaceAlt }}
      >
        <Image
          src={video.img}
          alt={video.title}
          fill
          className="object-contain object-center transition-transform duration-500 group-hover:scale-[1.03]"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
          unoptimized
        />
        <span
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex h-14 w-14 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            color: EJHEALTH_COLORS.dark,
          }}
        >
          <Play className="h-5 w-5 fill-current" />
        </span>
        {/* No duration chip. QA: "remove the video length for video items" —
            the same call made on /lj/video. `duration` stays on the fixtures
            and the type: it is real metadata, it is simply not on the card. */}
        {video.subscriberOnly && (
          <span
            className="absolute left-3 top-3 inline-flex items-center px-2 py-1"
            style={{
              ...EJHEALTH_TYPE.eyebrow,
              backgroundColor: EJHEALTH_COLORS.accent,
              color: EJHEALTH_COLORS.dark,
            }}
          >
            會員專享
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <CategoryEyebrow label={video.category} />
        <h3
          className="hkej-tap-title line-clamp-2"
          style={{ ...EJHEALTH_TYPE.h3Card, color: EJHEALTH_COLORS.dark, fontSize: 20 }}
        >
          {video.title}
        </h3>
        <span style={{ ...EJHEALTH_TYPE.meta, color: EJHEALTH_COLORS.textMuted }}>
          {video.time}
        </span>
      </div>
    </Link>
  );
}
